import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import FormDesign from "./FormDesign";
import { useAuth } from "../../../../firebase/AuthProvider";
import {
  getAllRoles,
  getUserById,
  updateUser,
} from "../../../../services/userServices/TeamService";
import Loader from "../../loader/Loader";
import Role from "../../../../interfaces/identity/Role";
import Region from "../../../../interfaces/identity/Region";
import { getAllRegions } from "../../../../services/userServices/UserInfoService";
import {
  getReportTo,
  getTeam,
} from "../../../../services/userServices/UserRolesService";
import {
  GetReportBody,
  GetTeamBody,
  reportTo,
  teams,
} from "../../../../interfaces/team/RegionHierarchy";
import { getAllTeamByRegions } from "../../../../services/calendarService/EventService";
import { ROLE_IDS, ROLE_PRIORITY, ROLES } from "../../../../Data/SD";
import Waiver from "../../../../interfaces/participant/Waiver";
import { getWaivers } from "../../../../services/participantServices/ParticipantService";
import { FormData, FormErrors, isInvalidWaiverFamilySelection } from "./AddUser";

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  onSuccess?: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  userId,
  onSuccess,
}) => {
  const { currentUser } = useAuth();
  const userUid = (currentUser as any)?.uid;

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [reportTo, setReportTo] = useState<reportTo[]>([]);
  const [waivers, setWaivers] = useState<Waiver[]>([]);
  const [teams, setTeams] = useState<teams[]>([]);

  const initialFormData: FormData = {
    firstName: "",
    lastName: "",
    email: "",
    displayName: "",
    phoneNumber: "",
    employeeId: "",
    returnSecureToken: true,
    Createdby: userUid,
    ManagerId: "",
    organizationId: 0,
    roleIds: [],
    regionIds: [],
    reportTo: [],
    TeamIds: [],
    file: null,
    timeZone: "",
    waiverId: null,
    waiverCode: "",
    waiverFamilyIds: [],
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [originalFormData, setOriginalFormData] = useState<FormData>(initialFormData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  // Function to compare current data with original data
  const hasChanges = useCallback(() => {
    const fieldsToCompare: (keyof FormData)[] = [
      'firstName', 'lastName', 'email', 'displayName', 'phoneNumber', 
      'employeeId', 'roleIds', 'regionIds', 'TeamIds', 'ManagerId', 
      'timeZone', 'waiverId', 'waiverFamilyIds'
    ];

    return fieldsToCompare.some(field => {
      const currentValue = formData[field];
      const originalValue = originalFormData[field];
      
      // Handle arrays comparison
      if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
        return JSON.stringify(currentValue.sort()) !== JSON.stringify(originalValue.sort());
      }
      
      // Handle other values
      return currentValue !== originalValue;
    });
  }, [formData, originalFormData]);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};
    const nameRegex = /^[A-Za-z''-]+(?: [A-Za-z''-]+)*$/;

    const validateNameField = (
      value: string,
      field: "firstName" | "lastName"
    ) => {
      const label = field === "firstName" ? "First Name" : "Last Name";

      if (!value?.trim()) {
        newErrors[field] = `${label} is required`;
        return;
      }

      const trimmed = value.trim();

      if (trimmed.length > 50) {
        newErrors[field] = `${label} cannot exceed 50 characters`;
        return;
      }

      if (!nameRegex.test(trimmed)) {
        newErrors[field] = `${label} contains invalid characters`;
        return;
      }

      if (/ {2,}/.test(trimmed)) {
        newErrors[field] = `${label} cannot contain multiple spaces`;
        return;
      }
    };

    validateNameField(formData.firstName, "firstName");
    validateNameField(formData.lastName, "lastName");

    if (!formData.displayName?.trim())
      newErrors.displayName = "Display Name is required";

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 10 digits";
    }

    if (!formData.employeeId?.trim())
      newErrors.employeeId = "Employee Id is required";

    if (!formData.roleIds || formData.roleIds.length === 0) {
      newErrors.roleIds = "At least one role must be selected";
    }

    if (!formData.regionIds || formData.regionIds.length === 0) {
      newErrors.regionIds = "At least one region must be selected";
    }

    if (
      formData.waiverFamilyIds.length > 0 &&
      isInvalidWaiverFamilySelection(formData.waiverFamilyIds, waivers)
    ) {
      newErrors.waiverFamilyIds = "OAAS and OCDD cannot be selected together";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, waivers]);

  const handleChange = useCallback(
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
        | {
            target: { name: string; value: any; type: string };
          }
    ) => {
      const { name, value, type } = e.target;

      if (name === "roleIds" && type === "checkbox-group") {
        const selectedRoleIds: number[] = value as number[];
        setFormData((prev) => ({
          ...prev,
          [name]: selectedRoleIds,
        }));
        setHasUnsavedChanges(true);
        return;
      }

      if (name === "waiverFamilyIds" && type === "checkbox-group") {
        const selectedWaiverFamilyIds: number[] = value as number[];
        setFormData((prev) => ({
          ...prev,
          [name]: selectedWaiverFamilyIds,
        }));
        setHasUnsavedChanges(true);
        return;
      }

      if (name === "TeamIds" && type === "checkbox-group") {
        setFormData((prev) => ({
          ...prev,
          [name]: value as number[],
        }));
        setHasUnsavedChanges(true);
        return;
      } else if (type === "file") {
        const inputElement = e.target as HTMLInputElement;
        const file = inputElement.files?.[0] || null;
        setFormData((prev) => ({
          ...prev,
          [name]: file,
        }));
        setHasUnsavedChanges(true);
      } else if (type === "checkbox") {
        const inputElement = e.target as HTMLInputElement;
        setFormData((prev) => ({
          ...prev,
          [name]: inputElement.checked,
        }));
        setHasUnsavedChanges(true);
      } else if (name === "waiverId") {
        // Handle waiverId specifically to also update waiverCode
        const selectedId = value ? Number(value) : null;
        const selectedWaiver = waivers.find((w) => w.id === selectedId);
        
        setFormData((prev) => ({
          ...prev,
          waiverId: selectedId,
          waiverCode: selectedWaiver?.waiverCode || "",
        }));
        setHasUnsavedChanges(true);
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
        setHasUnsavedChanges(true);
      }
    },
    [waivers]
  );

  // Separate function for programmatic updates that shouldn't trigger unsaved changes
  const updateFormDataProgrammatically = useCallback((updates: Partial<FormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Check if there are any changes
    if (!hasChanges()) {
      toast.info("No changes made to update.");
      return;
    }
    
    if (!validateForm()) return;
    if (!currentUser || !userId) return;
    const authToken = await currentUser.getIdToken();
    if (!authToken) {
      toast.error("Authentication error. Please log in again.");
      return;
    }
    setLoading(true);

    try {
      // Map employeeId to scId and TeamIds to teamIds for API compatibility
      const { employeeId, TeamIds, ...restFormData } = formData;
      const trimmedFormData = {
        ...restFormData,
        scId: employeeId,
        teamIds: TeamIds,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        displayName: formData.displayName.trim(),
        waiverFamilyIds: formData.waiverFamilyIds,
      };

      const userUpdateRes = await updateUser(trimmedFormData, userId, authToken);
      if (userUpdateRes.success) {
        toast.success(userUpdateRes.message || "User updated successfully!");
        setFormData(initialFormData);
        setOriginalFormData(initialFormData);
        setHasUnsavedChanges(false);
        setErrors({});
        onSuccess?.();
        onClose();
      } else {
        toast.error(userUpdateRes.message || "Failed to update user");
      }
    } catch (error: any) {
      console.error("Error updating user:", error);
      toast.error(
        error.message ||
          `Error updating user: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    setFormData(initialFormData);
    setOriginalFormData(initialFormData);
    setHasUnsavedChanges(false);
    setErrors({});
    onClose();
  }, [onClose]);

  // Fetch initial data (regions, waivers) FIRST
  // This ensures 'waivers' is populated BEFORE we try to map user data
  useEffect(() => {
    if (!isOpen || !currentUser) return;

    const fetchData = async () => {
      const authToken = await currentUser.getIdToken();
      if (!authToken) {
        console.warn("No auth token available for fetching data.");
        return;
      }
      setLoading(true);
      try {
        const [regionsData, waiverRes] = await Promise.all([
          getAllRegions(authToken),
          getWaivers(authToken),
        ]);

        if (regionsData) {
          setRegions(regionsData.data);
        } else {
          toast.error("Failed to load regions.");
        }
        if (waiverRes?.success && waiverRes.data?.length > 0) {
          console.log('WAIVERS', waiverRes.data)
          setWaivers(waiverRes.data);
        } else if (waiverRes?.success && waiverRes.data?.length === 0) {
          setWaivers([]);
          toast.info("No waiver families available.");
        } else {
          setWaivers([]);
          toast.error("Failed to load waiver families.");
        }
      } catch (error) {
        console.error("Error fetching regions or waiver families:", error);
        toast.error("Error fetching initial data.");
        setWaivers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, isOpen]);

  // Fetch user data when modal opens (Depends on waivers being loaded for proper filtering)
  useEffect(() => {
    if (!isOpen || !userId || !currentUser || waivers.length === 0) return;

    const fetchAllRolesAndUser = async () => {
      try {
        setLoading(true);
        const authToken = await currentUser.getIdToken();
        if (!authToken) return;

        const allRoles = await getAllRoles(authToken);
        if (allRoles) {
          setRoles(allRoles);

          const getById = await getUserById(userId, authToken);
          if (getById && getById.data) {
            const roleNames = getById.data.roles || [];
            const mappedRoleIds = roleNames
              .map((name: string) => {
                const role = allRoles.find(
                  (r: Role) => r.name.toUpperCase() === name.toUpperCase()
                );
                return role?.id || 0;
              })
              .filter((id) => id !== 0);

            const regionIdsFromApi = (getById.data.regions || []).map(
              (region: any) => region.id
            );

            // Initialize waiverFamilyIds from API response
            let waiverFamilyIds: number[] = [];
            
            // API returns waiverFamilyIds directly as array
            if (getById.data.waiverFamilyIds && Array.isArray(getById.data.waiverFamilyIds)) {
              // *** CLEANUP LOGIC ***
              // Filter out IDs that don't exist in the current waivers list
              // This prevents "Ghost IDs" (deleted waivers) from sticking
              waiverFamilyIds = getById.data.waiverFamilyIds.filter((id: number) => 
                   waivers.some((w: Waiver) => w.id === id)
              );
            }
            
            // Get first waiver code if available for form compatibility
            const waiverFamilyCodes = getById.data.waiverFamilyCode || [];
            const waiverCode = Array.isArray(waiverFamilyCodes) && waiverFamilyCodes.length > 0 
              ? waiverFamilyCodes[0] 
              : "";
            const waiver = waivers.find((w) => w.waiverCode === waiverCode);

            const updatedFormData = {
              firstName: getById.data.firstName || "",
              lastName: getById.data.lastName || "",
              email: getById.data.email || "",
              displayName: getById.data.displayName || "",
              phoneNumber: getById.data.phoneNumber || "",
              employeeId: getById.data.scId || "", // Map scId to employeeId for FormDesign
              returnSecureToken: true,
              Createdby: userUid,
              organizationId: getById.data.organizationId || 1,
              regionIds: regionIdsFromApi || [],
              roleIds: mappedRoleIds,
              reportTo: [],
              ManagerId: getById.data.managerId || "",
              TeamIds: getById.data.teams?.map((team: any) => team.id) || [],
              file: null,
              timeZone: getById.data.timezone || "",
              waiverCode: waiverCode,
              waiverId: waiver?.id || null,
              waiverFamilyIds: waiverFamilyIds,
            };
            
            setFormData(updatedFormData);
            setOriginalFormData(updatedFormData);
            setHasUnsavedChanges(false);
          }
        }
      } catch (error) {
        console.error("Error fetching roles or user:", error);
        toast.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };

    fetchAllRolesAndUser();
  }, [isOpen, userId, currentUser, waivers]); // Added waivers as dependency

  // Update waiverId when waivers are loaded and user has a waiverCode
  useEffect(() => {
    if (!formData.waiverCode || waivers.length === 0) return;
    const waiver = waivers.find((w) => w.waiverCode === formData.waiverCode);
    if (waiver && formData.waiverId !== waiver.id) {
      updateFormDataProgrammatically({ waiverId: waiver.id });
    }
  }, [waivers, formData.waiverCode, updateFormDataProgrammatically]);

  const fetchReportToList = async () => {
    if (!currentUser) return;
    const authToken = await currentUser.getIdToken();
    if (
      !authToken ||
      !formData.regionIds.length ||
      !formData.roleIds.length ||
      (!formData.TeamIds.length &&
        !formData.roleIds.includes(ROLE_IDS.PROGRAM_MANAGER))
    )
      return;

    const requestBody: GetReportBody = {
      regionIds: formData.regionIds,
      roleIds: formData.roleIds,
      teamIds: formData.TeamIds,
    };

    try {
      setReportTo([]);
      const reportToList = await getReportTo(authToken, requestBody);
      if (reportToList?.success) {
        setReportTo(reportToList.data);

        if (!reportToList.data.some((m) => m.id == formData.ManagerId)) {
          updateFormDataProgrammatically({ ManagerId: "" });
        }
      } else {
        toast.error(reportToList?.message);
      }
    } catch (error) {
      toast.error("Failed to fetch reporting users.");
      console.error(error);
    }
  };

  const fetchTeamsList = async () => {
    if (!currentUser) return;
    const authToken = await currentUser.getIdToken();
    if (!authToken || formData.regionIds.length == 0) return;
    try {
      const teamsList = await getAllTeamByRegions(
        authToken,
        formData.regionIds
      );
      if (teamsList) setTeams(teamsList);
    } catch (error) {
      toast.error("Failed to fetch teams.");
      console.error(error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchReportToList();
    }
  }, [formData.roleIds, formData.regionIds, formData.TeamIds, isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchTeamsList();
    }
  }, [formData.regionIds, isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {loading && <Loader />}
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto no-scrollbar bg-black bg-opacity-50"
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-[900px] overflow-y-auto rounded-2xl bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white rounded-t-2xl z-10">
            <div className="flex items-center justify-between border-b border-[#E5E7EB] py-4 px-6">
              <h2 className="m-0 block text-[#0A0A0A] font-poppins text-xl font-bold leading-tight">
                Edit User
              </h2>

              <button
                onClick={onClose}
                className="flex items-center text-[#0A0A0A] hover:text-gray-600 transition-colors"
              >
                <X size={12} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <FormDesign
              handleSubmit={handleSubmit}
              formData={formData}
              setFormData={setFormData}
              handleChange={handleChange}
              handleCancel={handleCancel}
              roles={roles}
              regions={regions}
              reportTo={reportTo}
              teams={teams}
              waivers={waivers}
              errors={errors}
              hasChanges={hasUnsavedChanges}
              hasDisabledFieldForUpdating={true} 
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EditUserModal;